import { pickTransferPackage, type TransferRouteConfig } from "@majourneys/shared";

/**
 * What the transport pages know about a route beyond the API row: its place
 * in the list, its group, its icon, where it goes, and its URL. Everything
 * here is derived from the slug and the route's own settings; prices always
 * come from the API.
 */

/** The city whose routes the hub sells. Marrakech is the only one for now. */
export const TRANSPORT_CITY = "marrakech";

/**
 * The order routes appear in (slugs without the city prefix). The airport
 * comes first because it is what most guests book; the API returns routes by
 * title, which put "Agafay return" at the top. Unknown slugs go last.
 */
export const ROUTE_ORDER = [
  "airport-transfer",
  "casablanca-airport-transfer",
  "casablanca-transfer",
  "essaouira-transfer",
  "imlil-transfer",
  "city-centre-transfer",
  "dinner-transfer",
  "nightclub-transfer",
  "agafay-transfer",
  "agafay-return-transfer",
  "private-driver-half-day",
  "private-driver-full-day",
];

function bareSlug(slug: string): string {
  return slug.replace(/^marrakech-/, "");
}

export function routeRank(slug: string): number {
  const i = ROUTE_ORDER.indexOf(bareSlug(slug));
  return i === -1 ? ROUTE_ORDER.length : i;
}

/** A copy of the routes in display order. */
export function sortRoutes<R extends { slug: string }>(routes: readonly R[]): R[] {
  return [...routes].sort((a, b) => routeRank(a.slug) - routeRank(b.slug));
}

export type RouteGroupKey = "airport_cities" | "evenings" | "by_the_hour";

export const GROUP_ORDER: RouteGroupKey[] = ["airport_cities", "evenings", "by_the_hour"];

/** Slug fragments per group. Checked in this order; a route matching none goes to airport_cities. */
const GROUP_MATCHERS: Array<[RouteGroupKey, string[]]> = [
  ["by_the_hour", ["private-driver"]],
  ["evenings", ["dinner", "nightclub", "agafay"]],
  ["airport_cities", ["airport", "casablanca", "imlil", "essaouira", "city-centre"]],
];

export function routeGroup(slug: string): RouteGroupKey {
  for (const [key, needles] of GROUP_MATCHERS) {
    if (needles.some((n) => slug.includes(n))) return key;
  }
  return "airport_cities";
}

/** The routes split into their groups, in group order, empty groups left out. */
export function groupRoutes<R extends { slug: string }>(routes: readonly R[]): Array<{ key: RouteGroupKey; routes: R[] }> {
  return GROUP_ORDER.map((key) => ({ key, routes: routes.filter((r) => routeGroup(r.slug) === key) })).filter(
    (g) => g.routes.length > 0,
  );
}

/** A Remix icon name for the route's row and its route line. */
export function routeIcon(slug: string, config?: Pick<TransferRouteConfig, "secondPlace" | "trip">): string {
  const s = bareSlug(slug);
  if (config?.secondPlace === "restaurant" || s.includes("dinner")) return "ri-restaurant-line";
  if (config?.secondPlace === "club" || s.includes("nightclub")) return "ri-music-2-line";
  if (config?.secondPlace === "camp" || s.includes("agafay")) return "ri-fire-line";
  if (config?.trip === "hourly" || s.includes("private-driver")) return "ri-steering-2-line";
  if (s.includes("airport")) return "ri-plane-line";
  if (s.includes("essaouira")) return "ri-sailboat-line";
  if (s.includes("imlil")) return "ri-landscape-line";
  return "ri-building-line";
}

/** A ride's own page, as router parts. `people` is carried over from the hub's quote bar. */
export function ridePageParts(lang: string, slug: string, people?: number) {
  return {
    to: "/$lang/transport/$slug" as const,
    params: { lang, slug },
    search: people !== undefined ? { people } : {},
  };
}

/** A ride's own page, as a path (no query). */
export function ridePagePath(lang: string, slug: string): string {
  return `/${lang}/transport/${slug}`;
}

/** The ride page's search params: `?people=<n>` opens the form on that party size. */
export interface RideSearch {
  people?: number;
}

/**
 * Keeps `people` when it is a whole number from 1 to 99; anything else is
 * dropped. Above 17 (`TRANSFER_MAX_GUESTS`) is kept on purpose: the form
 * then says to message us on WhatsApp rather than quietly showing 17.
 */
export function parseRideSearch(search: Record<string, unknown>): RideSearch {
  const raw = search.people;
  const n = typeof raw === "number" ? raw : typeof raw === "string" && /^\d{1,3}$/.test(raw.trim()) ? Number(raw) : NaN;
  return Number.isInteger(n) && n >= 1 && n <= MAX_LINK_PEOPLE ? { people: n } : {};
}

/** The largest `?people=` a ride link keeps. */
const MAX_LINK_PEOPLE = 99;

/** The price the route charges `people` (the smallest vehicle that seats them), or null. */
export function priceFor(route: { packages: ReadonlyArray<{ maxGuests: number | null; price: number }> }, people: number) {
  return pickTransferPackage(route.packages, people)?.price ?? null;
}

type RouteLike = { slug: string; config: Pick<TransferRouteConfig, "trip" | "secondPlace"> };

/**
 * A one-way ride and its there-and-back twin, when both are sold (Agafay one
 * way and Agafay there and back): the ride card's "Ride back the same
 * night" switch moves between them. Only a real pair counts: same second place, one `one_way`,
 * one `return`. Any other ride has no switch; nothing is priced by doubling.
 */
export function rideBackPair<R extends RouteLike>(routes: readonly R[], slug: string): { oneWay: R; back: R } | null {
  const bare = bareSlug(slug);
  const oneWayBare = bare.endsWith("-return-transfer") ? bare.replace(/-return-transfer$/, "-transfer") : bare;
  if (!oneWayBare.endsWith("-transfer")) return null;
  const backBare = oneWayBare.replace(/-transfer$/, "-return-transfer");
  const oneWay = routes.find((r) => bareSlug(r.slug) === oneWayBare);
  const back = routes.find((r) => bareSlug(r.slug) === backBare);
  if (!oneWay || !back) return null;
  if (oneWay.config.trip !== "one_way" || back.config.trip !== "return") return null;
  if (oneWay.config.secondPlace !== back.config.secondPlace) return null;
  return { oneWay, back };
}

